import { useEffect, useRef, useState, useCallback } from 'react';
import './aiChatWidget.css';
import axios from 'axios';
import { fetchSharepointDocuments, prepareDocumentsForGemini, isValidSharepointUrl, formatFileSize } from './sharepointService';
export default function AiChatWidget({ denuncia, estados, reclamos, reclamoSeleccionado }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [errorConfig, setErrorConfig] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [iaKey, setIaKey] = useState(null);
  const [iaPrompt, setIaPrompt] = useState(null);
  // SharePoint state
  const [spDocs, setSpDocs] = useState([]);
  const [spLoading, setSpLoading] = useState(false);
  const [spError, setSpError] = useState(null);
  const [spLoaded, setSpLoaded] = useState(false);
  const [spFolderName, setSpFolderName] = useState('');
  const spFetchedRef = useRef(false);

  // Claim state
  const [claimDocs, setClaimDocs] = useState([]);
  const [claimDocsLoading, setClaimDocsLoading] = useState(false);
  const [claimDocsError, setClaimDocsError] = useState(null);
  const [claimDocsLoaded, setClaimDocsLoaded] = useState(false);
  const claimDocsFetchedRef = useRef(false);

  // Extra context states (Ver Todo, Siniestros/Otros, Valores Estado)
  const [verTodoData, setVerTodoData] = useState(null);
  const [otrosSiniestrosData, setOtrosSiniestrosData] = useState(null);
  const [valoresEstadoData, setValoresEstadoData] = useState(null);

  useEffect(() => {
    const fetchExtraContext = async () => {
      const id = denuncia?.id;
      if (!id) {
        setVerTodoData(null);
        setOtrosSiniestrosData(null);
        setValoresEstadoData(null);
        return;
      }
      
      try {
        const resVT = await axios.get(`https://back.agsseguros.online/api/verTodoEditar/${id}`);
        setVerTodoData(resVT.data);
      } catch (err) {
        console.error("Error fetching Ver Todo:", err);
      }

      try {
        const resSin = await axios.get(`https://back.agsseguros.online/api/siniestros/${id}`);
        setOtrosSiniestrosData(resSin.data);
      } catch (err) {
        console.error("Error fetching Siniestros:", err);
      }

      try {
        const resVal = await axios.get(`https://back.agsseguros.online/api/prueba/valoresEstadoDeUnStro/${id}`);
        setValoresEstadoData(resVal.data);
      } catch (err) {
        console.error("Error fetching Valores Estado:", err);
      }
    };

    fetchExtraContext();
  }, [denuncia?.id]);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 4 * 1024 * 1024) {
      alert('El archivo supera el límite de 4MB.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setAttachedFile({
        name: file.name,
        type: file.type,
        base64: reader.result.split(',')[1],
        dataUrl: reader.result,
      });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleRemoveFile = () => {
    setAttachedFile(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const iaDb = await axios.get('https://back.agsseguros.online/ai');
        setIaKey(iaDb.data.data.API_KEY);
        setIaPrompt(iaDb.data.data.PROMPT);
      } catch (error) {
        console.error('Error fetching data', error);
      }
    };
    fetchData();
  }, []);

  // Fetch SharePoint docs on demand (triggered by quick reply)
  const loadSharepointDocs = useCallback(async () => {
    const link = denuncia?.link;
    if (!link || !isValidSharepointUrl(link) || spFetchedRef.current) return;
    spFetchedRef.current = true;
    setSpLoading(true);
    setSpError(null);

    // Add a loading system message
    const loadingMsgId = `msg-sp-loading-${Date.now()}`;
    setMessages(prev => [
      ...prev,
      {
        id: loadingMsgId,
        role: 'assistant',
        content: '📂 Conectando con SharePoint... Leyendo documentos del siniestro.',
        timestamp: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
        isSpLoading: true,
      },
    ]);

    try {
      const result = await fetchSharepointDocuments(link);

      // Remove loading message
      setMessages(prev => prev.filter(m => m.id !== loadingMsgId));

      if (result.success && result.archivos.length > 0) {
        setSpDocs(result.archivos);
        setSpLoaded(true);
        setSpFolderName(result.carpetaNombre || 'Carpeta SharePoint');

        // Add success message listing docs
        const docListText = result.archivos
          .map((a, i) => `- ${a.nombre}`)
          .join('\n');
        setMessages(prev => [
          ...prev,
          {
            id: `msg-sp-result-${Date.now()}`,
            role: 'assistant',
            content: `📂 **SharePoint conectado** — Se cargaron **${result.archivos.length}** documento${result.archivos.length > 1 ? 's' : ''} de la carpeta "${result.carpetaNombre || 'Siniestro'}":\n\n${docListText}\n\nPodés preguntarme sobre el contenido de estos archivos.`,
            timestamp: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
            isSpResult: true,
          },
        ]);
      } else {
        setSpError(result.error || 'No se encontraron documentos.');
        setMessages(prev => [
          ...prev,
          {
            id: `msg-sp-error-${Date.now()}`,
            role: 'assistant',
            content: `📂 **SharePoint:** ${result.error || 'No se encontraron documentos en la carpeta del siniestro.'}`,
            timestamp: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
          },
        ]);
      }
    } catch (err) {
      console.error('Error loading SP docs:', err);
      setMessages(prev => prev.filter(m => m.id !== loadingMsgId));
      setSpError('Error inesperado al conectar con SharePoint.');
    } finally {
      setSpLoading(false);
    }
  }, [denuncia?.link]);

  // Get active claim ID dynamically
  const getActiveClaimId = () => {
    if (reclamoSeleccionado) {
      const id = reclamoSeleccionado.idReclamo || reclamoSeleccionado.id || reclamoSeleccionado.reclamo3ro?.id;
      if (id) return id;
    }
    if (denuncia?.idReclamo) return denuncia.idReclamo;

    const recList = Array.isArray(reclamos) ? reclamos : (reclamos ? [reclamos] : []);
    if (recList.length > 0) {
      const r = recList[0];
      return r?.idReclamo || r?.id || r?.reclamo3ro?.id;
    }
    return null;
  };
  const activeClaimId = getActiveClaimId();

  // Fetch Claim documents on demand
  const loadClaimDocs = useCallback(async () => {
    const claimId = activeClaimId;
    if (!claimId || claimDocsFetchedRef.current) return;
    claimDocsFetchedRef.current = true;
    setClaimDocsLoading(true);
    setClaimDocsError(null);

    // Helpers inside callback
    const getMimeTypeFromExt = (ext) => {
      const cleanExt = ext ? ext.toLowerCase().replace('.', '') : '';
      if (cleanExt === 'pdf') return 'application/pdf';
      if (cleanExt === 'jpeg' || cleanExt === 'jpg') return 'image/jpeg';
      if (cleanExt === 'png') return 'image/png';
      if (cleanExt === 'gif') return 'image/gif';
      if (cleanExt === 'webp') return 'image/webp';
      return 'application/octet-stream';
    };

    const isSupportedType = (mimeType) => {
      const supported = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      return supported.includes(mimeType);
    };

    // Add a loading system message
    const loadingMsgId = `msg-claim-loading-${Date.now()}`;
    setMessages(prev => [
      ...prev,
      {
        id: loadingMsgId,
        role: 'assistant',
        content: `📂 Conectando con el servidor... Leyendo documentación del reclamo #${claimId}.`,
        timestamp: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
        isClaimLoading: true,
      },
    ]);

    try {
      const response = await axios.get(`https://reclamosback.agsseguros.online/apiReclamos/files/agente/${claimId}`, {
        timeout: 60000,
      });

      let rawFiles = [];
      if (response.data) {
        if (response.data.documentos && Array.isArray(response.data.documentos)) {
          rawFiles = response.data.documentos;
        } else if (Array.isArray(response.data)) {
          rawFiles = response.data;
        } else if (response.data.archivos && Array.isArray(response.data.archivos)) {
          rawFiles = response.data.archivos;
        }
      }

      if (rawFiles.length === 0) {
        // Remove loading message
        setMessages(prev => prev.filter(m => m.id !== loadingMsgId));
        setClaimDocsError('No se encontraron documentos en este reclamo.');
        setMessages(prev => [
          ...prev,
          {
            id: `msg-claim-error-${Date.now()}`,
            role: 'assistant',
            content: `📂 **Documentación del Reclamo:** No se encontraron archivos vinculados al reclamo #${claimId}.`,
            timestamp: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
          },
        ]);
        setClaimDocsLoading(false);
        return;
      }

      const MAX_FILE_SIZE = 4 * 1024 * 1024;
      const MAX_FILES = 10;

      const filesToDownload = rawFiles
        .map(file => {
          const ext = file.extension || (file.nombre ? file.nombre.split('.').pop() : '');
          const mimeType = getMimeTypeFromExt(ext);
          const size = file.tamanoBytes || file.size || 0;
          return {
            ...file,
            mimeType,
            size,
          };
        })
        .filter(file => isSupportedType(file.mimeType) && file.size <= MAX_FILE_SIZE && file.downloadUrl)
        .slice(0, MAX_FILES);

      if (filesToDownload.length === 0) {
        // Remove loading message
        setMessages(prev => prev.filter(m => m.id !== loadingMsgId));
        setClaimDocsError('No se encontraron documentos con formatos soportados para análisis.');
        setMessages(prev => [
          ...prev,
          {
            id: `msg-claim-error-${Date.now()}`,
            role: 'assistant',
            content: `📂 **Documentación del Reclamo:** El reclamo #${claimId} no tiene archivos de formatos compatibles (PDF, JPG, PNG, GIF, WEBP) o superan el límite de 4MB.`,
            timestamp: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
          },
        ]);
        setClaimDocsLoading(false);
        return;
      }

      // Download files in parallel
      const downloadPromises = filesToDownload.map(async (file) => {
        try {
          const fileRes = await axios.get(file.downloadUrl, {
            responseType: 'arraybuffer',
            timeout: 30000,
          });

          let binary = '';
          const bytes = new Uint8Array(fileRes.data);
          const len = bytes.byteLength;
          for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          const base64 = window.btoa(binary);

          return {
            nombre: file.nombre,
            mimeType: file.mimeType || fileRes.headers['content-type'] || 'application/octet-stream',
            base64,
            size: file.size || bytes.byteLength,
          };
        } catch (downloadErr) {
          console.error(`Error downloading file ${file.nombre}:`, downloadErr);
          return null;
        }
      });

      const downloadedFiles = (await Promise.all(downloadPromises)).filter(Boolean);

      // Remove loading message
      setMessages(prev => prev.filter(m => m.id !== loadingMsgId));

      if (downloadedFiles.length > 0) {
        setClaimDocs(downloadedFiles);
        setClaimDocsLoaded(true);

        const docListText = downloadedFiles
          .map((a, i) => `- ${a.nombre}`)
          .join('\n');
        setMessages(prev => [
          ...prev,
          {
            id: `msg-claim-result-${Date.now()}`,
            role: 'assistant',
            content: `📂 **Documentación del Reclamo cargada** — Se cargaron **${downloadedFiles.length}** archivo${downloadedFiles.length > 1 ? 's' : ''} del reclamo #${claimId}:\n\n${docListText}\n\nPodés preguntarme sobre el contenido de esta documentación.`,
            timestamp: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
            isClaimResult: true,
          },
        ]);
      } else {
        setClaimDocsError('Error al descargar los documentos del reclamo.');
        setMessages(prev => [
          ...prev,
          {
            id: `msg-claim-error-${Date.now()}`,
            role: 'assistant',
            content: `📂 **Documentación del Reclamo:** Ocurrió un error al descargar la documentación para análisis del reclamo #${claimId}.`,
            timestamp: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
          },
        ]);
      }
    } catch (err) {
      console.error('Error loading claim docs:', err);
      setMessages(prev => prev.filter(m => m.id !== loadingMsgId));
      setClaimDocsError(err.response?.data?.error || err.message || 'Error inesperado al conectar con el servidor.');
      setMessages(prev => [
        ...prev,
        {
          id: `msg-claim-error-${Date.now()}`,
          role: 'assistant',
          content: `❌ **Error al cargar documentación del reclamo #${claimId}:** ${err.response?.data?.error || err.message || 'Error al conectar con el servidor.'}`,
          timestamp: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
        },
      ]);
    } finally {
      setClaimDocsLoading(false);
    }
  }, [activeClaimId]);

  // Prepare claim documents for Gemini context
  const prepareClaimDocumentsForGemini = (archivos) => {
    if (!archivos || archivos.length === 0) return { contextText: '', parts: [] };

    let contextText = `\n=== DOCUMENTACIÓN DEL RECLAMO VINCULADO (${archivos.length} archivo${archivos.length > 1 ? 's' : ''}) ===\n`;
    const parts = [];

    archivos.forEach((archivo, idx) => {
      contextText += `- Archivo ${idx + 1}: "${archivo.nombre}" (${archivo.mimeType}, ${formatFileSize(archivo.size)})\n`;

      const supported = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (archivo.base64 && supported.includes(archivo.mimeType)) {
        parts.push({
          inlineData: {
            mimeType: archivo.mimeType,
            data: archivo.base64,
          },
        });
      }
    });

    return { contextText, parts };
  };

  // Set initial welcome message
  useEffect(() => {
    setMessages([
      {
        id: 'msg-initial',
        role: 'assistant',
        content: `¡Hola! Soy tu asistente de Inteligencia Artificial para **AGS Seguros**. 

Puedo ayudarte a analizar la información de este siniestro. ¿Qué te gustaría saber?`,
        timestamp: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
      },
    ]);
    // Reset SP state when siniestro changes
    spFetchedRef.current = false;
    setSpDocs([]);
    setSpLoaded(false);
    setSpError(null);
    setSpFolderName('');

    // Reset Claim state when siniestro changes
    claimDocsFetchedRef.current = false;
    setClaimDocs([]);
    setClaimDocsLoaded(false);
    setClaimDocsError(null);
  }, [denuncia?.id]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  // Check config status when chat is opened
  useEffect(() => {
    if (isOpen && !isMinimized) {
      const apiKey = iaKey;
      setErrorConfig(!apiKey);
      // Auto focus input
      setTimeout(() => {
        if (inputRef.current) inputRef.current.focus();
      }, 300);
    }
  }, [isOpen, isMinimized, iaKey]);

  // Format claim details
  const formatSiniestroData = (denuncia, estados, reclamos) => {
    let text = `=== DATOS DE LA DENUNCIA ===\n`;
    text += `ID Siniestro: ${denuncia?.id || 'N/A'}\n`;
    text += `Nro Siniestro: ${denuncia?.nroStro || 'N/A'}\n`;
    text += `Entry ID: ${denuncia?.entryId || 'N/A'}\n`;
    text += `Tipo de Siniestro: ${denuncia?.tipoStro || 'N/A'}\n`;
    text += `Fecha de Siniestro: ${denuncia?.fechaStro || 'N/A'}\n`;
    text += `Fecha de Recepción: ${denuncia?.fechaRecepcion || 'N/A'}\n`;
    text += `Asegurado: ${denuncia?.nombreAseg || ''} ${denuncia?.apellidoAseg || ''}\n`;
    text += `Vehículo Asegurado: ${denuncia?.marca || ''} ${denuncia?.modelo || ''} (Patente: ${denuncia?.patente || 'N/A'})\n`;
    text += `Relato del Hecho: ${denuncia?.relato || denuncia?.descripcion || 'N/A'}\n\n`;

    text += `=== ESTADOS Y SEGUIMIENTO ===\n`;
    if (estados && estados.length > 0) {
      estados.forEach((e, idx) => {
        text += `- Estado ${idx + 1}: ${e.estado} (Fecha: ${e.fecha || 'N/A'})\n`;
      });
    } else {
      text += `No hay estados registrados.\n`;
    }
    text += `\n`;

    text += `=== CRONOLOGÍA DE EVENTOS ===\n`;
    const crono = denuncia?.cronologia || [];
    if (crono.length > 0) {
      crono.forEach(c => {
        if (c.borrar !== 1) {
          text += `[${c.fecha} ${c.hora}] Autor: ${c.autor} | Motivo: ${c.motivo || 'N/A'}\n`;
          if (c.desc) text += `  Sistema: ${c.desc}\n`;
          if (c.obs) text += `  Obs Usuario: ${c.obs}\n`;
        }
      });
    } else {
      text += `No hay eventos registrados en la cronología.\n`;
    }
    text += `\n`;

    text += `=== RECLAMOS DE TERCEROS ===\n`;
    const recList = Array.isArray(reclamos) ? reclamos : reclamos ? [reclamos] : [];
    if (recList.length > 0) {
      recList.forEach((r, idx) => {
        text += `- Reclamo #${idx + 1}: ID ${r.idReclamo || 'N/A'} | Tercero: ${r.nombre3ro || ''} ${r.apellido3ro || ''} (Patente: ${r.patente3ro || 'N/A'})\n`;
        text += `  Estado Reclamo: ${r.estadoNuevoName || 'N/A'}\n`;
      });
    } else {
      text += `No hay reclamos de terceros asociados.\n`;
    }
    text += `\n`;

    // Add active selected claim if present
    if (reclamoSeleccionado) {
      const r = reclamoSeleccionado;
      text += `=== DETALLES DEL RECLAMO DE TERCERO SELECCIONADO (VISTA RECLAMO ACTUAL) ===\n`;
      text += `ID Reclamo: ${r.idReclamo || 'N/A'}\n`;
      text += `Nombre del Tercero: ${r.nombre3ro || ''} ${r.apellido3ro || ''}\n`;
      text += `DNI / CUIT: ${r.dni3ro || 'N/A'}\n`;
      text += `Teléfono: ${r.telefono3ro || 'N/A'}\n`;
      text += `Email: ${r.email3ro || 'N/A'}\n`;
      text += `Dirección: ${r.direccion3ro || 'N/A'}\n`;
      text += `Vehículo del Tercero: ${r.marca3ro || ''} ${r.modelo3ro || ''} (Patente: ${r.patente3ro || 'N/A'})\n`;
      text += `Compañía de Seguros: ${r.cia3ro || 'N/A'}\n`;
      text += `Nro de Póliza: ${r.nroPoliza3ro || 'N/A'}\n`;
      text += `Estado del Reclamo: ${r.estadoNuevoName || 'N/A'}\n`;

      // Add document status if present
      if (r.docs) {
        text += `Documentos del Tercero:\n`;
        Object.entries(r.docs).forEach(([docKey, docVal]) => {
          if (docKey.startsWith('estado')) {
            const docName = docKey.replace('estado', '');
            text += `  - ${docName}: ${docVal === 902 ? 'Aprobado' : docVal === 903 ? 'Rechazado' : docVal === 904 ? 'En Revisión' : 'Pendiente'}\n`;
          }
        });
      }
      text += `\n`;
    }

    // Helper: format ver todo
    const formatVerTodoData = (data) => {
      if (!data) return '';
      let text = `=== DETALLES VER TODO (EDICIÓN Y COMPLETO) ===\n`;
      text += `Póliza: ${data.pza || 'N/A'}\n`;
      text += `Productor (PAS): ${data.pas || 'N/A'}\n`;
      text += `Email Asegurado: ${data.email || 'N/A'}\n`;
      text += `Fecha Carga: ${data.fechaCarga || 'N/A'}\n`;
      text += `Fecha Denuncia: ${data.fechaDenuncia || 'N/A'}\n`;
      text += `Hora Siniestro: ${data.horaStro || 'N/A'}\n`;
      text += `Dirección Hecho: ${data.direccionStroCalle || ''} ${data.direccionStroNumero || ''}, ${data.direccionStroLocalidad || ''}, ${data.direccionStroProvincia || ''}\n`;
      if (data.interseccionStro || data.interseccionStro2) {
        text += `Esquina / Intersección: ${data.interseccionStro || ''} y ${data.interseccionStro2 || ''}\n`;
      }
      text += `Detalles del Siniestro (Ver Todo): ${data.detallesStro || 'N/A'}\n`;
      text += `Conductor Asegurado: ${data.nombreConductorAseg || 'N/A'} (DNI: ${data.dniConductorAseg || 'N/A'}, Tel: ${data.telefonoConductorAseg || 'N/A'})\n`;
      text += `Relación con Asegurado: ${data.relacionConAseg || 'N/A'}\n`;
      text += `Registro Conductor Asegurado: ${data.registroConductorAseg || 'N/A'} (Vence: ${data.vencimientoConductorAseg || 'N/A'})\n`;
      text += `Daños Asegurado: ${data.detallesDaniosAseg || 'N/A'}\n`;
      
      for (let i = 1; i <= 3; i++) {
        const propTercero = data[`propietarioTercero${i}`] || data[`conductorTercero${i}`];
        if (propTercero) {
          text += `\n--- Tercero #${i} ---\n`;
          text += `Nombre Conductor/Propietario: ${data[`conductorTercero${i}`] || data[`propietarioTercero${i}`] || 'N/A'}\n`;
          text += `DNI Tercero: ${data[`dniConductorTercero${i}`] || data[`dniTercero${i}`] || 'N/A'}\n`;
          text += `Teléfono Tercero: ${data[`telefonoConductorTercero${i}`] || data[`telefonoTercero${i}`] || 'N/A'}\n`;
          text += `Vehículo Tercero: ${data[`marcaTercero${i}`] || ''} ${data[`modeloTercero${i}`] || ''} (Patente/Dominio: ${data[`dominioTercero${i}`] || 'N/A'})\n`;
          text += `Compañía/Póliza Tercero: ${data[`ciaTercero${i}`] || 'N/A'} (Póliza: ${data[`polizaTercero${i}`] || 'N/A'})\n`;
          text += `Daños Tercero: ${data[`danioTercero${i}`] || data[`detallesDanioTercero${i}`] || 'N/A'}\n`;
        }
      }
      text += `\n`;
      return text;
    };

    // Helper: format otros siniestros
    const formatOtrosSiniestrosData = (data) => {
      if (!data || !data.otrasDenuncias) return '';
      let text = `=== OTROS SINIESTROS RELACIONADOS ===\n`;
      
      const porPoliza = data.otrasDenuncias.porPoliza || [];
      text += `Siniestros con la misma Póliza (${porPoliza.length}):\n`;
      if (porPoliza.length > 0) {
        porPoliza.forEach((p, idx) => {
          text += `- #${idx + 1}: Nro Stro: ${p.nroStro || 'N/A'} | Asegurado: ${p.nombreConductorAseg || 'N/A'} | Patente: ${p.dom || 'N/A'} | Fecha Stro: ${p.fechaStro || 'N/A'} | Tipo: ${p.catStro || 'N/A'}\n`;
        });
      } else {
        text += `No hay otros siniestros con la misma póliza.\n`;
      }
      
      const porPatente = data.otrasDenuncias.porPatente || [];
      text += `Siniestros con la misma Patente (${porPatente.length}):\n`;
      if (porPatente.length > 0) {
        porPatente.forEach((p, idx) => {
          text += `- #${idx + 1}: Nro Stro: ${p.nroStro || 'N/A'} | Asegurado: ${p.nombreConductorAseg || 'N/A'} | Póliza: ${p.pza || 'N/A'} | Fecha Stro: ${p.fechaStro || 'N/A'} | Tipo: ${p.catStro || 'N/A'}\n`;
        });
      } else {
        text += `No hay otros siniestros con la misma patente.\n`;
      }
      text += `\n`;
      return text;
    };

    // Helper: format valores estado / slide cargas
    const formatValoresEstadoData = (data) => {
      if (!data) return '';
      let text = `=== VALORES DE ESTADOS Y PARÁMETROS CONTRACTUALES ===\n`;
      
      const recList = data.reclamos || [];
      if (recList.length > 0) {
        recList.forEach((r, idx) => {
          text += `- Reclamo #${idx + 1} (ID Reclamo: ${r.idReclamo || r.id || 'N/A'}): Mapeo de Valores de Estados:\n`;
          text += `  Monto Reclamado: ${r.monto || 'N/A'}\n`;
          text += `  Monto Aceptado (Aceptación): ${r.aceptacion || 'N/A'}\n`;
          text += `  Porcentaje Responsabilidad: ${r.porcentaje || 'N/A'}%\n`;
          text += `  Orden de Pago: ${r.ordenDePago || 'N/A'}\n`;
          text += `  Comercial: ${r.comercial || 'N/A'}\n`;
          
          if (r.valores && typeof r.valores === 'object') {
            text += `  Valores específicos del flujo de estados:\n`;
            Object.entries(r.valores).forEach(([key, val]) => {
              if (val !== null && val !== undefined && val !== '') {
                text += `    * ${key}: ${val}\n`;
              }
            });
          }
        });
      } else {
        text += `No hay valores contractuales detallados de reclamos.\n`;
      }
      text += `\n`;
      return text;
    };

    text += formatVerTodoData(verTodoData);
    text += formatOtrosSiniestrosData(otrosSiniestrosData);
    text += formatValoresEstadoData(valoresEstadoData);

    return text;
  };

  const handleCopy = (id, content) => {
    navigator.clipboard.writeText(content);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatResponseText = text => {
    if (!text) return '';
    let escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // Bold formatting **text**
    escaped = escaped.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Bullet formatting
    escaped = escaped.replace(/^\s*-\s+(.*?)$/gm, '<li>$1</li>');
    escaped = escaped.replace(/(<li>.*?<\/li>)/gs, '<ul>$1</ul>');
    escaped = escaped.replace(/<\/ul>\s*<ul>/g, '');

    // Newlines
    escaped = escaped.replace(/\n/g, '<br />');
    return escaped;
  };

  const handleSubmit = async e => {
    if (e) e.preventDefault();
    if (!input.trim() && !attachedFile) return;
    if (isTyping) return;

    const userMsg = input.trim();
    setInput('');
    const currentAttachment = attachedFile;
    setAttachedFile(null);

    const newMsgId = `msg-user-${Date.now()}`;
    const userMsgObj = {
      id: newMsgId,
      role: 'user',
      content: userMsg || `[Archivo adjunto: ${currentAttachment.name}]`,
      timestamp: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
      file: currentAttachment,
    };

    setMessages(prev => [...prev, userMsgObj]);
    setIsTyping(true);

    try {
      const apiKey = iaKey;
      const customPrompt = iaPrompt || 'Eres un asistente de IA para AGS Seguros. Analiza los datos del siniestro y responde la consulta del usuario.';
      const aiModel = localStorage.getItem('ai_model') || 'gemini-2.5-flash';

      if (!apiKey) {
        throw new Error('La API Key de Gemini no está configurada. Diríjase a Ajustes.');
      }

      const contextText = formatSiniestroData(denuncia, estados, reclamos);

      // Include SharePoint documents context if loaded
      let spContextText = '';
      let spParts = [];
      if (spDocs.length > 0) {
        const spData = prepareDocumentsForGemini(spDocs);
        spContextText = spData.contextText;
        spParts = spData.parts;
      }

      // Include Claim documents context if loaded
      let claimContextText = '';
      let claimParts = [];
      if (claimDocs.length > 0) {
        const claimData = prepareClaimDocumentsForGemini(claimDocs);
        claimContextText = claimData.contextText;
        claimParts = claimData.parts;
      }

      const systemInstruction = `${customPrompt}\n\nINFORMACIÓN DEL SINIESTRO:\n${contextText}${spContextText}${claimContextText}`;

      const contents = [];
      messages.forEach(msg => {
        if (msg.id === 'msg-initial') return;

        const parts = [{ text: msg.content }];
        if (msg.file) {
          parts.push({
            inlineData: {
              mimeType: msg.file.type,
              data: msg.file.base64,
            },
          });
        }

        contents.push({
          role: msg.role === 'user' ? 'user' : 'model',
          parts,
        });
      });

      const currentParts = [{ text: userMsg || 'Analiza esta documentación.' }];
      if (currentAttachment) {
        currentParts.push({
          inlineData: {
            mimeType: currentAttachment.type,
            data: currentAttachment.base64,
          },
        });
      }

      // Add SP document inline data parts to the current user message
      if (spParts.length > 0) {
        currentParts.push(...spParts);
      }

      // Add Claim document inline data parts to the current user message
      if (claimParts.length > 0) {
        currentParts.push(...claimParts);
      }

      contents.push({
        role: 'user',
        parts: currentParts,
      });

      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${aiModel}:generateContent?key=${apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents,
          systemInstruction: {
            parts: [{ text: systemInstruction }],
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData?.error?.message || `Error de Gemini (Código ${response.status})`;
        throw new Error(message);
      }

      const data = await response.json();
      const aiText = data?.candidates?.[0]?.content?.parts?.[0]?.text;

      if (!aiText) {
        throw new Error('No se recibió respuesta de Gemini.');
      }

      const aiMsgObj = {
        id: `msg-ai-${Date.now()}`,
        role: 'assistant',
        content: aiText,
        timestamp: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
      };

      setMessages(prev => [...prev, aiMsgObj]);
    } catch (err) {
      console.error(err);
      const errorMsgObj = {
        id: `msg-err-${Date.now()}`,
        role: 'assistant',
        content: `❌ **Error:** ${err.message || 'No se pudo obtener una respuesta.'}`,
        timestamp: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
      };
      setMessages(prev => [...prev, errorMsgObj]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleQuickReply = promptText => {
    setInput(promptText);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };

  const quickReplies = [
    { id: 'qr-resumen', label: 'Resumir Siniestro', prompt: 'Por favor, haz un resumen breve de este siniestro.' },
    { id: 'qr-cronologia', label: 'Analizar Cronología', prompt: 'Analiza la cronología del siniestro e indica si hay algún conflicto o retraso importante.' },
    {
      id: 'qr-analizar-reclamo',
      label: 'Analizar Reclamo',
      prompt: reclamoSeleccionado
        ? 'Analiza en detalle la información de este reclamo de tercero seleccionado (datos personales, patente, compañía y estado de sus documentos) e indica su estado actual y qué gestiones faltan.'
        : 'Analiza los reclamos de terceros asociados a este siniestro y resume su estado y documentación.',
    },
    { id: 'qr-documentos', label: 'Chequear Documentos', prompt: 'Revisa si de acuerdo al relato y reclamos, falta algún documento o si están todos al día.' },
    ...(denuncia?.link && isValidSharepointUrl(denuncia.link) && !spLoaded && !spLoading
      ? [
          {
            id: 'qr-sp-cargar',
            label: '📂 Cargar Docs SharePoint',
            prompt: '',
            isSp: true,
            isSpLoad: true,
          },
        ]
      : []),
    ...(spLoaded
      ? [
          {
            id: 'qr-sp-analizar',
            label: '📂 Analizar Docs SharePoint',
            prompt: 'Analiza todos los documentos de SharePoint que tenés cargados de este siniestro. Haz un resumen del contenido de cada archivo y destaca información relevante.',
            isSp: true,
          },
        ]
      : []),
    ...(activeClaimId && !claimDocsLoaded && !claimDocsLoading
      ? [
          {
            id: 'qr-claim-cargar',
            label: '📂 Cargar Docs Reclamo',
            prompt: '',
            isClaim: true,
            isClaimLoad: true,
          },
        ]
      : []),
    ...(claimDocsLoaded
      ? [
          {
            id: 'qr-claim-analizar',
            label: '📂 Analizar Docs Reclamo',
            prompt: `Analiza todos los documentos del reclamo #${activeClaimId} que tenés cargados. Haz un resumen de la documentación presentada y destaca información relevante para la liquidación.`,
            isClaim: true,
          },
        ]
      : []),
  ];

  return (
    <div className='ai-widget-container'>
      {isOpen && !isMinimized ? (
        <div className={`ai-chat-panel ${isExpanded ? 'expanded' : ''}`}>
          {/* Header */}
          <div className='ai-chat-header'>
            <div className='ai-chat-header-avatar'>
              <i className='bi bi-robot'></i>
            </div>
            <div className='ai-chat-header-info'>
              <span className='ai-chat-header-title'>Asistente IA</span>
              <div className='ai-chat-header-status'>
                <span className='ai-chat-header-dot'></span>
                <span>En línea</span>
                {spLoaded && (
                  <span className='ai-sp-header-badge'>
                    <i className='bi bi-folder-fill'></i> {spDocs.length} doc{spDocs.length > 1 ? 's' : ''}
                  </span>
                )}
                {claimDocsLoaded && (
                  <span className='ai-claim-header-badge'>
                    <i className='bi bi-file-earmark-medical-fill'></i> {claimDocs.length} doc{claimDocs.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
            <div className='ai-chat-header-actions'>
              {activeClaimId && (
                <button
                  type='button'
                  className={`ai-chat-header-btn ai-claim-header-btn ${claimDocsLoaded ? 'loaded' : ''} ${claimDocsLoading ? 'loading' : ''}`}
                  onClick={!claimDocsLoading && !claimDocsLoaded ? loadClaimDocs : undefined}
                  disabled={claimDocsLoading || claimDocsLoaded}
                  title={claimDocsLoaded ? `Reclamo: ${claimDocs.length} documentos cargados` : claimDocsLoading ? 'Cargando documentos...' : 'Cargar documentación del reclamo'}
                >
                  {claimDocsLoading ? <span className='ai-sp-spinner'></span> : <i className='bi bi-file-earmark-medical-fill'></i>}
                </button>
              )}
              {denuncia?.link && isValidSharepointUrl(denuncia.link) && (
                <button
                  type='button'
                  className={`ai-chat-header-btn ai-sp-header-btn ${spLoaded ? 'loaded' : ''} ${spLoading ? 'loading' : ''}`}
                  onClick={!spLoading && !spLoaded ? loadSharepointDocs : undefined}
                  disabled={spLoading || spLoaded}
                  title={spLoaded ? `SharePoint: ${spDocs.length} documentos cargados` : spLoading ? 'Cargando documentos...' : 'Cargar documentos de SharePoint'}
                >
                  {spLoading ? <span className='ai-sp-spinner'></span> : <i className='bi bi-folder-symlink-fill'></i>}
                </button>
              )}
              <button type='button' className='ai-chat-header-btn' onClick={() => setIsExpanded(!isExpanded)} title={isExpanded ? 'Reducir tamaño' : 'Expandir tamaño'}>
                <i className={isExpanded ? 'bi bi-fullscreen-exit' : 'bi bi-arrows-fullscreen'}></i>
              </button>
              <button type='button' className='ai-chat-header-btn' onClick={() => setIsMinimized(true)} title='Minimizar'>
                <i className='bi bi-dash-lg'></i>
              </button>
              <button type='button' className='ai-chat-header-btn' onClick={() => setIsOpen(false)} title='Cerrar'>
                <i className='bi bi-x-lg'></i>
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className='ai-chat-messages'>
            {errorConfig && (
              <div className='alert alert-warning p-2 m-2' style={{ fontSize: '11px' }}>
                <i className='bi bi-exclamation-triangle-fill me-1'></i>
                <strong>API Key no configurada.</strong> Vaya a Ajustes para agregar su Gemini API Key.
              </div>
            )}

            {messages.map(msg => (
              <div key={msg.id} className={`ai-message-row ${msg.role}`}>
                <div className={`ai-message-bubble ${msg.isSpResult ? 'ai-sp-result-bubble' : ''} ${msg.isSpLoading ? 'ai-sp-loading-bubble' : ''} ${msg.isClaimResult ? 'ai-claim-result-bubble' : ''} ${msg.isClaimLoading ? 'ai-claim-loading-bubble' : ''}`}>
                  {(msg.isSpLoading || msg.isClaimLoading) && (
                    <div className='ai-sp-loading-indicator'>
                      <span className='ai-sp-loading-spinner'></span>
                    </div>
                  )}
                  {msg.file && (
                    <div className='ai-message-file-attachment mb-2'>
                      {msg.file.type.startsWith('image/') ? (
                        <img src={msg.file.dataUrl} alt={msg.file.name} className='ai-message-image-preview' />
                      ) : (
                        <div className='ai-message-pdf-chip d-flex align-items-center gap-2'>
                          <i className='bi bi-file-earmark-pdf-fill text-danger' style={{ fontSize: '20px' }}></i>
                          <span className='ai-pdf-name text-truncate'>{msg.file.name}</span>
                        </div>
                      )}
                    </div>
                  )}
                  <div dangerouslySetInnerHTML={{ __html: formatResponseText(msg.content) }}></div>
                </div>

                <div className='ai-message-meta'>
                  <span>{msg.timestamp}</span>
                  {msg.role === 'assistant' && msg.id !== 'msg-initial' && !msg.isSpLoading && !msg.isClaimLoading && (
                    <button type='button' className='ai-message-action-btn' onClick={() => handleCopy(msg.id, msg.content)} title='Copiar'>
                      {copiedId === msg.id ? <i className='bi bi-check-lg text-success'></i> : <i className='bi bi-clipboard'></i>}
                    </button>
                  )}
                </div>
              </div>
            ))}

            {isTyping && (
              <div className='ai-message-row assistant'>
                <div className='ai-message-bubble' style={{ background: '#f1f5f9' }}>
                  <div className='ai-typing-indicator'>
                    <span className='ai-typing-dot'></span>
                    <span className='ai-typing-dot'></span>
                    <span className='ai-typing-dot'></span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick Replies */}
          <div className='ai-quick-replies'>
            {quickReplies.map(qr => {
              let btnClass = '';
              if (qr.isSpLoad) btnClass = 'ai-sp-quick-reply';
              else if (qr.isSp) btnClass = 'ai-sp-quick-reply-analyze';
              else if (qr.isClaimLoad) btnClass = 'ai-claim-quick-reply';
              else if (qr.isClaim) btnClass = 'ai-claim-quick-reply-analyze';

              const handleClick = () => {
                if (qr.isSpLoad) {
                  loadSharepointDocs();
                } else if (qr.isClaimLoad) {
                  loadClaimDocs();
                } else {
                  handleQuickReply(qr.prompt);
                }
              };

              return (
                <button
                  key={qr.id}
                  type='button'
                  className={`ai-quick-reply-btn ${btnClass}`}
                  onClick={handleClick}
                >
                  {qr.label}
                </button>
              );
            })}
          </div>

          {/* Input Form */}
          <div className='ai-chat-input-container'>
            <form onSubmit={handleSubmit} className='ai-chat-form flex-column align-items-stretch' style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
              {attachedFile && (
                <div className='ai-attached-file-preview d-flex align-items-center justify-content-between mb-2'>
                  <div className='d-flex align-items-center gap-2'>
                    <i className={attachedFile.type.startsWith('image/') ? 'bi bi-image text-primary' : 'bi bi-file-earmark-pdf text-danger'} style={{ fontSize: '16px' }}></i>
                    <span className='ai-file-name text-truncate' style={{ maxWidth: '240px', fontSize: '11px', color: '#475569' }}>
                      {attachedFile.name}
                    </span>
                  </div>
                  <button type='button' className='btn-close' style={{ fontSize: '8px', padding: '4px' }} onClick={handleRemoveFile}></button>
                </div>
              )}
              <div className='d-flex align-items-center gap-2 w-100' style={{ display: 'flex', width: '100%', alignItems: 'center', gap: '8px' }}>
                <button type='button' className='ai-chat-attach-btn' onClick={() => fileInputRef.current?.click()} disabled={isTyping || errorConfig} title='Adjuntar documentación'>
                  <i className='bi bi-paperclip'></i>
                </button>
                <input ref={fileInputRef} type='file' accept='image/*,application/pdf' style={{ display: 'none' }} onChange={handleFileChange} />
                <input
                  ref={inputRef}
                  type='text'
                  className='ai-chat-input'
                  style={{ flexGrow: 1 }}
                  placeholder={attachedFile ? 'Consulta sobre este archivo...' : 'Escribe tu consulta...'}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  disabled={errorConfig}
                />
                <button type='submit' className='ai-chat-send-btn' disabled={(!input.trim() && !attachedFile) || isTyping || errorConfig}>
                  <i className='bi bi-send-fill' style={{ fontSize: '12px' }}></i>
                </button>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className='ai-chat-footer'>Asistente con tecnología Gemini AI. Valide la información importante.</div>
        </div>
      ) : (
        <button
          type='button'
          className='ai-widget-trigger'
          onClick={() => {
            setIsOpen(true);
            setIsMinimized(false);
          }}
          title='Asistente de IA'>
          <i className='bi bi-chat-square-text-fill'></i>
        </button>
      )}
    </div>
  );
}
